import React, { memo } from 'react';
import styled from 'styled-components';
import * as styles from './styles';
import { TableProps } from './models';
import { useExpanded, useGroupBy, useSortBy, useTable } from 'react-table';
import { Header } from './components/header/Header';
import { Body } from './components/body/Body';

export const Table = styled(
  <TData extends object>({
    className,
    dataTestId,
    actionsMenu,
    groupBy,
    renderExpandableTemplate,
    data,
    columns,
    expandRowId,
    onExpandRow,
    onCollapseRow,
    emptyStateTemplate,
    getRowId,
    lazyLoadingOptions
  }: TableProps<TData>) => {
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      state: { expanded, sortBy },
      toggleGroupBy,
      setGroupBy,
      toggleAllRowsExpanded,
      toggleRowExpanded,
    } = useTable(
      {
        columns,
        data,
        groupBy: groupBy,
        getRowId: getRowId as (row: TData) => string,
        autoResetExpanded: false,
      },
      useGroupBy,
      useSortBy,
      useExpanded
    );

    const tableProps = getTableProps();

    const emptyState = data.length === 0;

    React.useEffect(() => {
      toggleGroupBy(groupBy, true);
    }, [data]);

    React.useEffect(() => {
      toggleGroupBy(groupBy, true);
      setGroupBy([groupBy]);
    }, [groupBy]);

    React.useEffect(() => {
      if (expandRowId) {
        rows?.forEach((row) => {
          if (row.id === expandRowId) {
            row?.toggleRowExpanded(true);
            onExpandRow?.(row);
          } else {
            if (row.isExpanded) {
              onCollapseRow?.(row);
            }
            row?.toggleRowExpanded(false);
          }
        });
      }
    }, [rows, expandRowId]);

    return (
      <table {...{ className, ...tableProps, 'data-testid': dataTestId }}>
        <Header
          {...{
            headerGroups,
            groupBy,
            hasExpandableArea: !!renderExpandableTemplate,
            hasActionsMenu: !!actionsMenu,
            sortBy,
          }}
        />
        <Body
          {...{
            bodyProps: getTableBodyProps(),
            actionsMenu,
            rows,
            emptyState,
            groupBy,
            expanded,
            toggleAllRowsExpanded,
            toggleRowExpanded,
            emptyStateTemplate,
            prepareRow,
            onCollapseRow,
            onExpandRow,
            columns,
            renderExpandableTemplate,
            lazyLoadingOptions,
          }}
        />
      </table>
    );
  }
)`
  ${styles.Table}
`;

Table.defaultProps = {
  emptyStateTemplate: <></>,
};

export const TableV3 = memo(Table);
